import { useEffect, useRef } from "react";
import { isTypingTarget } from "../../lib/keyboard";

// 信箱頁的快捷鍵(規格 §2.6):j/k 上下、Enter 開、r 回覆、a 全部回覆、f 轉寄、s 星號、
// e 封存、# 刪除、c 寫信、/ 搜尋、u 回列表、? 說明。
//
// 不作用的時候:
// - 焦點在輸入框、文字區、下拉選單、可編輯區(正在打字);
// - 有對話框開著(含快捷鍵說明本身;window.confirm 開著時頁面本來就收不到按鍵);
// - 按著 Ctrl / ⌘ / Alt —— 那些是瀏覽器與系統的快捷鍵(⌘K 全域搜尋等),一律不攔。
// - Enter 落在按鈕 / 連結上:瀏覽器自己會「按下去」,這裡再開一次就重複了。

export type MailShortcut =
  | "next"
  | "prev"
  | "open"
  | "reply"
  | "replyAll"
  | "forward"
  | "star"
  | "archive"
  | "delete"
  | "compose"
  | "search"
  | "back"
  | "help";

const KEYS: Record<string, MailShortcut> = {
  j: "next",
  k: "prev",
  Enter: "open",
  r: "reply",
  a: "replyAll",
  f: "forward",
  s: "star",
  e: "archive",
  "#": "delete",
  c: "compose",
  "/": "search",
  u: "back",
  "?": "help",
};

/** 說明裡列出來的(順序就是畫面上的順序)。 */
export const SHORTCUT_HELP: [string, string][] = [
  ["j / k", "下一封 / 上一封"],
  ["Enter", "打開選到的信"],
  ["r", "回覆"],
  ["a", "全部回覆"],
  ["f", "轉寄"],
  ["s", "加 / 去星號"],
  ["e", "封存"],
  ["#", "刪除"],
  ["c", "寫信"],
  ["/", "搜尋"],
  ["u", "回列表"],
  ["?", "顯示這份說明"],
];

function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]') !== null;
}

function activatable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return tag === "button" || tag === "a" || tag === "summary" || target.getAttribute("role") === "button";
}

export function useMailShortcuts(handler: (action: MailShortcut, event: KeyboardEvent) => void, enabled = true) {
  const saved = useRef(handler);
  saved.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
      if (isTypingTarget(event.target)) return;
      if (dialogOpen()) return;
      const action = KEYS[event.key];
      if (!action) return;
      if (action === "open" && activatable(event.target)) return;
      event.preventDefault();
      saved.current(action, event);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
